import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import { getDialCode } from '@ab-platform/ui';
import { PillButton } from '@ab-platform/ui/pill-button';
import { Banner } from '@ab-platform/ui/banner';
import { Checkbox } from '@ab-platform/ui/checkbox';
import { DataList, DataListRow } from '@ab-platform/ui/data-list';
import { FieldError } from '@ab-platform/ui/field-error';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { FormSection } from '@ab-platform/ui/form-section';
import { Label } from '@ab-platform/ui/label';
import { env } from '@/lib/env';
import {
  submitOnboardingRequest,
  type FieldIssue,
} from '@/lib/api';
import { buildPayload } from '@/lib/submit-payload';
import {
  buildSuccessPath,
  persistLastRequestId,
} from '@/lib/onboarding-success-nav';
import { type RegistrationFormValues } from '@/hooks/useRegistrationForm';
import { useWizardSessionIdContext } from '@/hooks/WizardSessionIdContext';
import { persistDocumentsPageStateToSession } from '@/lib/documents-state-recovery';
import { useAreaSession } from '@/hooks/AreaSessionContext';
import { useDraftAutosaveHandle } from '@/hooks/useDraftAutosave';
import { getAreaMe, submitAreaRequest, type AreaFailure } from '@/lib/areaApi';
import { performAreaSubmit } from '@/lib/areaSubmitFlow';
import { issueTargetLabel } from '@/lib/correctionIssuesView';
import { buildMissingDocumentsCard } from '@/lib/onboardingUxViews';

const STEP_BACK_BY_PATH_HEAD: Record<string, string> = {
  organization: '/registrazione-intermediario/organizzazione',
  orgContacts: '/registrazione-intermediario/contatti',
  addresses: '/registrazione-intermediario/indirizzi',
  adminUser: '/registrazione-intermediario/amministratore',
};

const WITHHOLDING_LABELS: Record<string, string> = {
  ORDINARIA_23_50: 'Ordinaria — 23% su 50% imponibile',
  RIDOTTA_23_20: 'Ridotta — 23% su 20% imponibile',
  FORFETTARIO_0: 'Forfettario — esente',
  PRESTAZIONE_OCCASIONALE_20: 'Prestazione occasionale 20%',
};

function formatWithholding(value: string | undefined | null): string {
  if (!value) return '—';
  return WITHHOLDING_LABELS[value] ?? value;
}

function formatPhone(entry: { prefixIso: string; number: string }): string {
  if (!entry.number || entry.number.trim().length === 0) return '—';
  const dial = getDialCode(entry.prefixIso);
  return dial ? `+${dial} ${entry.number.trim()}` : entry.number.trim();
}

export function SummaryPage(): JSX.Element {
  const navigate = useNavigate();
  const wizardSessionId = useWizardSessionIdContext();
  const form = useFormContext<RegistrationFormValues>();
  const { register, watch, setValue, formState, trigger, handleSubmit, setError } = form;
  const values = watch();
  const isPerson = values.organization.legalNature === 'PERSON';

  // PR-ONB-PRECHECK-3 — AREA MODE: flag on + session bound to a request →
  // the submit goes to the area endpoint (no payload on the wire, no
  // Turnstile, no legacy upload-token flow). Flag off / anonymous session →
  // the legacy path below stays byte-identical.
  const session = useAreaSession();
  const autosave = useDraftAutosaveHandle();
  const areaMode = session.enabled && session.bind !== null;

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // PR-ONB-WIZARD-AREA-UX-1 — labels of required documents missing at submit
  // time; drives the actionable "Vai ai documenti" card (vs a dead-end error).
  const [missingDocs, setMissingDocs] = useState<string[] | null>(null);

  // Flush the draft (no data loss) then jump to the area documents section so
  // the applicant can upload the missing required documents.
  const goToDocuments = async (): Promise<void> => {
    if (autosave) await autosave.flushNow();
    navigate('/area/documenti');
  };

  const onSubmit = async (formValues: RegistrationFormValues): Promise<void> => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const payload = {
        ...buildPayload(formValues),
        wizardSessionId: wizardSessionId || undefined,
      };
      const result = await submitOnboardingRequest(payload);
      if (result.ok) {
        // Persist the reference id so the success page survives a hard refresh
        // (URL query + sessionStorage). PR-ONBOARDING-SUCCESS-REF-PERSIST-1.
        persistLastRequestId(result.submissionId);
        if (result.uploadToken !== undefined) {
          // Persist a sessionStorage copy of the documents-page state
          // BEFORE navigating. The router-state is the primary path,
          // but a hard refresh on the documents page wipes it; the
          // sessionStorage fallback (and the URL-hash recovery channel
          // an operator may construct) keeps the upload flow alive.
          // See `lib/documents-state-recovery.ts` for the full priority
          // order + cleanup contract.
          const documentsState = {
            uploadToken: result.uploadToken,
            legalNature: formValues.organization.legalNature,
            orgType: formValues.organization.orgType,
            submissionId: result.submissionId,
          };
          persistDocumentsPageStateToSession(documentsState);
          // PR-DIDIT-6B: land on the dedicated identity-verification step
          // first; "Continua ai documenti" forwards the same state to
          // /documenti. The same post-submit state + recovery channels back
          // both pages.
          navigate('/registrazione-intermediario/verifica-identita', {
            replace: true,
            state: documentsState,
          });
        } else {
          navigate(buildSuccessPath(result.submissionId), {
            replace: true,
            state: { submissionId: result.submissionId },
          });
        }
        return;
      }
      handleSubmitError(result.code, result.fieldIssues, result.submissionId);
    } finally {
      setSubmitting(false);
    }
  };

  // ── AREA submit (PR-ONB-PRECHECK-3) ────────────────────────────────────────
  //
  // Orchestration extracted to `performAreaSubmit` (token-freshness contract):
  // flush autosave → reloadUser → getIdToken(true) → GET /me (fresh token) →
  // POST /request/submit (same fresh token). Self-sufficient: an email
  // verified in another tab is reflected without bouncing through Stato.
  const onAreaSubmit = async (): Promise<void> => {
    setSubmitError(null);
    setMissingDocs(null);
    setSubmitting(true);
    try {
      const outcome = await performAreaSubmit({
        flush: () => (autosave ? autosave.flushNow() : Promise.resolve(true)),
        reloadUser: () => session.reloadUser(),
        getToken: (force) => session.getToken(force),
        getMe: (token) => getAreaMe(token),
        submit: (token, body) => submitAreaRequest(token, body),
      });
      switch (outcome.kind) {
        case 'OK':
          navigate('/area/stato', { replace: true });
          return;
        case 'FLUSH_DIRTY':
          setSubmitError(
            'Le ultime modifiche non risultano salvate. Riprova il salvataggio prima di inviare.',
          );
          return;
        case 'NO_SESSION':
          setSubmitError('Sessione scaduta: accedi di nuovo dalla tua area.');
          return;
        case 'ME_UNAVAILABLE':
          setSubmitError('Impossibile leggere lo stato della richiesta. Riprova.');
          return;
        case 'EMAIL_NOT_VERIFIED':
          setSubmitError(
            'Devi verificare l\'indirizzo email prima di inviare la richiesta: controlla la casella e poi riprova.',
          );
          return;
        case 'SUBMIT_FAILED':
          handleAreaSubmitError(outcome.failure);
          return;
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAreaSubmitError = (failure: AreaFailure): void => {
    switch (failure.kind) {
      case 'EMAIL_NOT_VERIFIED':
        setSubmitError(
          'Devi verificare l\'indirizzo email prima di inviare la richiesta: controlla la casella e poi riprova dalla pagina Stato.',
        );
        return;
      case 'EMAIL_MISMATCH':
        setSubmitError(
          'L\'email del referente deve coincidere con l\'email verificata del tuo account.',
        );
        return;
      case 'SUBMIT_VALIDATION_FAILED': {
        const labels = (failure.validationIssues ?? [])
          .slice(0, 5)
          .map((i) => issueTargetLabel(i.path))
          .join(', ');
        setSubmitError(
          labels.length > 0
            ? `Alcuni dati non sono completi o validi: ${labels}. Torna ai passaggi precedenti per correggerli.`
            : 'Alcuni dati non sono completi o validi. Torna ai passaggi precedenti per correggerli.',
        );
        return;
      }
      case 'DOCUMENTS_INCOMPLETE': {
        // Actionable card with a CTA to the area documents section, instead of
        // a dead-end error message (PR-ONB-WIZARD-AREA-UX-1).
        const labels = (failure.missingDocuments ?? []).map((d) =>
          issueTargetLabel(d.documentType),
        );
        setMissingDocs(labels);
        return;
      }
      case 'CORRECTIONS_INCOMPLETE': {
        const targets = (failure.unresolvedIssues ?? [])
          .map((i) => issueTargetLabel(i.targetKey))
          .join(', ');
        setSubmitError(
          targets.length > 0
            ? `Completa tutte le correzioni richieste prima di ripresentare la domanda: ${targets}.`
            : 'Completa tutte le correzioni richieste prima di ripresentare la domanda.',
        );
        return;
      }
      case 'COOLDOWN_ACTIVE':
        setSubmitError(
          'La tua precedente richiesta è stata respinta: non è ancora possibile presentarne una nuova.',
        );
        return;
      case 'ACCEPTANCE_VERSION_MISMATCH':
        setSubmitError(
          'Le condizioni da accettare sono state aggiornate. Ricarica la pagina e ripresenta la richiesta.',
        );
        return;
      case 'DRAFT_CONFLICT':
        setSubmitError(
          'La richiesta è stata aggiornata da un\'altra sessione. Ricarica la pagina e riprova.',
        );
        return;
      case 'RUI_CHECK_FAILED':
        setSubmitError(
          failure.message ??
            'La verifica del numero RUI non è andata a buon fine: controlla i dati inseriti.',
        );
        return;
      case 'NOT_EDITABLE':
        setSubmitError(
          'La richiesta non è più modificabile in questo stato. Controlla lo stato nella tua area.',
        );
        return;
      case 'RATE_LIMITED':
        setSubmitError('Troppe richieste, riprova più tardi.');
        return;
      default:
        setSubmitError('Impossibile inviare la richiesta. Riprova più tardi.');
    }
  };

  const handleSubmitError = (
    code: 'VALIDATION_FAILED' | 'TURNSTILE_INVALID' | 'RATE_LIMITED' | 'NETWORK_ERROR' | 'UNKNOWN',
    fieldIssues?: FieldIssue[],
    submissionId?: string,
  ): void => {
    // Surface the submissionId in the error banner so the user can quote
    // it in support tickets even when the submission failed validation.
    const ref = submissionId
      ? ` Riferimento richiesta: ${submissionId}.`
      : '';
    if (code === 'VALIDATION_FAILED' && fieldIssues && fieldIssues.length > 0) {
      // Re-attach BE issues to RHF + jump to the earliest step with an error.
      for (const issue of fieldIssues) {
        const path = issue.path.join('.');
        setError(path as never, { message: issue.message });
      }
      const head = fieldIssues
        .map((i) => i.path[0])
        .find((h): h is string => typeof h === 'string' && h in STEP_BACK_BY_PATH_HEAD);
      if (head) navigate(STEP_BACK_BY_PATH_HEAD[head]!);
      setSubmitError(
        `Verifica i dati: alcuni campi non sono validi.${ref}`,
      );
      return;
    }
    if (code === 'TURNSTILE_INVALID') {
      setSubmitError(
        `Verifica sicurezza non valida. Aggiorna la pagina e riprova.${ref}`,
      );
      return;
    }
    if (code === 'RATE_LIMITED') {
      setSubmitError(`Troppe richieste, riprova più tardi.${ref}`);
      return;
    }
    setSubmitError(
      `Impossibile inviare la richiesta. Riprova più tardi.${ref}`,
    );
  };

  return (
    <FocusedPageShell
      title="Quasi fatto: rivedi e invia"
      description="Una volta inviata, riceverai una email di conferma e ti contatteremo per i prossimi passaggi."
      onBack={() => navigate('/registrazione-intermediario/fiscale')}
      footer={
        <PillButton
          type="button"
          size="lg"
          disabled={submitting}
          onClick={() => {
            if (areaMode) {
              // No Turnstile in area mode (the session is authenticated; the
              // /register bind already passed it). The schema field still
              // requires a non-empty string, so satisfy it with a local
              // placeholder that NEVER leaves the client — the area submit
              // body carries no token and no payload.
              setValue('turnstileToken', 'area-session', {
                shouldValidate: false,
              });
              void trigger().then((ok) => {
                if (ok) void onAreaSubmit();
              });
              return;
            }
            void trigger().then((ok) => {
              if (ok) void handleSubmit(onSubmit)();
            });
          }}
          className="w-full"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Invio…
            </>
          ) : areaMode ? (
            'Invia la richiesta'
          ) : (
            'Continua ai documenti'
          )}
        </PillButton>
      }
    >
      {submitError ? <Banner tone="danger">{submitError}</Banner> : null}

      {missingDocs ? (
        <Banner
          tone="warning"
          title={buildMissingDocumentsCard(missingDocs).title}
        >
          <div className="grid gap-2">
            {missingDocs.length > 0 ? (
              <ul className="list-disc pl-5">
                {missingDocs.map((doc) => (
                  <li key={doc}>{doc}</li>
                ))}
              </ul>
            ) : (
              <span>
                Carica i documenti obbligatori richiesti per la tua tipologia.
              </span>
            )}
            <div>
              <PillButton
                type="button"
                size="sm"
                onClick={() => void goToDocuments()}
              >
                {buildMissingDocumentsCard(missingDocs).ctaLabel}
              </PillButton>
            </div>
          </div>
        </Banner>
      ) : null}

      <FormSection title="Organizzazione">
        <DataList>
          <DataListRow
            label="Natura giuridica"
            value={isPerson ? 'Persona fisica' : 'Società'}
          />
          <DataListRow
            label={isPerson ? 'Nome e cognome' : 'Ragione sociale'}
            value={values.organization.legalName}
          />
          {!isPerson ? (
            <DataListRow
              label="Tipo impresa"
              value={values.organization.businessLegalForm}
            />
          ) : null}
          <DataListRow label="Tipologia" value={values.organization.orgType} />
          <DataListRow label="Codice fiscale" value={values.organization.fiscalCode} />
          <DataListRow label="Partita IVA" value={values.organization.vatNumber} />
          <DataListRow label="Numero RUI" value={values.organization.ruiNumber} />
          <DataListRow
            label="Iscrizione RUI"
            value={values.organization.ruiRegistrationDate}
          />
        </DataList>
      </FormSection>

      <FormSection title="Informazioni fiscali">
        <DataList>
          <DataListRow
            label="Ritenuta d'acconto"
            value={formatWithholding(values.organization.withholdingType)}
          />
        </DataList>
      </FormSection>

      <FormSection title="Amministratore">
        <DataList>
          <DataListRow label="Nome e cognome" value={values.adminUser.fullName} />
          <DataListRow label="Email" value={values.adminUser.email} />
          <DataListRow label="Codice fiscale" value={values.adminUser.fiscalCode} />
          <DataListRow label="Sesso" value={values.adminUser.sex} />
          <DataListRow label="Data di nascita" value={values.adminUser.dateOfBirth} />
          <DataListRow label="Paese di nascita" value={values.adminUser.birthCountry} />
          <DataListRow
            label="Comune di nascita"
            value={
              values.adminUser.birthCountry === 'IT'
                ? values.adminUser.placeOfBirth
                : (values.adminUser.placeOfBirth ?? '')
            }
          />
          {values.adminUser.birthCountry === 'IT' &&
          values.adminUser.birthMunicipalityCadastralCode ? (
            <DataListRow
              label="Codice catastale"
              value={values.adminUser.birthMunicipalityCadastralCode}
            />
          ) : null}
          {values.adminUser.mobilePhone.number ? (
            <DataListRow
              label="Cellulare"
              value={formatPhone(values.adminUser.mobilePhone)}
            />
          ) : null}
        </DataList>
      </FormSection>

      <FormSection title="Contatti">
        <DataList>
          <DataListRow label="Email" value={values.orgContacts.email} />
          <DataListRow label="PEC" value={values.orgContacts.pec} />
          {values.orgContacts.phone.number ? (
            <DataListRow
              label="Telefono fisso"
              value={formatPhone(values.orgContacts.phone)}
            />
          ) : null}
          {values.orgContacts.mobile.number ? (
            <DataListRow
              label={isPerson ? 'Cellulare' : 'Cellulare organizzazione'}
              value={formatPhone(values.orgContacts.mobile)}
            />
          ) : null}
        </DataList>
      </FormSection>

      <FormSection title={isPerson ? 'Residenza' : 'Sede legale'}>
        <DataList>
          <DataListRow
            label="Via e civico"
            value={values.addresses.primary.street}
          />
          <DataListRow label="Città" value={values.addresses.primary.city} />
          <DataListRow
            label="Provincia"
            value={values.addresses.primary.province}
          />
          <DataListRow label="CAP" value={values.addresses.primary.zip} />
          <DataListRow label="Paese" value={values.addresses.primary.country} />
        </DataList>
      </FormSection>

      {!isPerson && values.addresses.alternateOffice.enabled ? (
        <FormSection title="Sede operativa">
          <DataList>
            <DataListRow
              label="Via e civico"
              value={values.addresses.alternateOffice.data.street}
            />
            <DataListRow
              label="Città"
              value={values.addresses.alternateOffice.data.city}
            />
            <DataListRow
              label="Provincia"
              value={values.addresses.alternateOffice.data.province}
            />
            <DataListRow
              label="CAP"
              value={values.addresses.alternateOffice.data.zip}
            />
            <DataListRow
              label="Paese"
              value={values.addresses.alternateOffice.data.country}
            />
          </DataList>
        </FormSection>
      ) : null}

      {!isPerson && values.addresses.mailingAddress.enabled ? (
        <FormSection title="Sede di corrispondenza">
          <DataList>
            <DataListRow
              label="Via e civico"
              value={values.addresses.mailingAddress.data.street}
            />
            <DataListRow
              label="Città"
              value={values.addresses.mailingAddress.data.city}
            />
            <DataListRow
              label="Provincia"
              value={values.addresses.mailingAddress.data.province}
            />
            <DataListRow
              label="CAP"
              value={values.addresses.mailingAddress.data.zip}
            />
            <DataListRow
              label="Paese"
              value={values.addresses.mailingAddress.data.country}
            />
          </DataList>
        </FormSection>
      ) : null}

      {isPerson && values.addresses.alternateResidence.enabled ? (
        <FormSection title="Domicilio">
          <DataList>
            <DataListRow
              label="Via e civico"
              value={values.addresses.alternateResidence.data.street}
            />
            <DataListRow
              label="Città"
              value={values.addresses.alternateResidence.data.city}
            />
            <DataListRow
              label="Provincia"
              value={values.addresses.alternateResidence.data.province}
            />
            <DataListRow
              label="CAP"
              value={values.addresses.alternateResidence.data.zip}
            />
            <DataListRow
              label="Paese"
              value={values.addresses.alternateResidence.data.country}
            />
          </DataList>
        </FormSection>
      ) : null}

      <div className="grid gap-3">
        <Label className="flex items-start gap-3 text-sm font-normal text-foreground">
          <Checkbox
            className="mt-0.5"
            checked={values.acceptanceConfirmed === true}
            {...register('acceptanceConfirmed')}
          />
          <span>Confermo la correttezza dei dati inseriti.</span>
        </Label>
        <FieldError message={formState.errors.acceptanceConfirmed?.message} />
      </div>

      {areaMode ? null : (
      <div className="grid gap-2">
        <Turnstile
          siteKey={env.VITE_TURNSTILE_SITE_KEY}
          onSuccess={(token) => {
            setValue('turnstileToken', token, { shouldValidate: true });
          }}
          onError={() => {
            setValue('turnstileToken', '', { shouldValidate: false });
            void trigger('turnstileToken');
          }}
          onExpire={() => {
            setValue('turnstileToken', '', { shouldValidate: false });
          }}
          options={{ theme: 'light' }}
        />
        <FieldError message={formState.errors.turnstileToken?.message} />
      </div>
      )}
    </FocusedPageShell>
  );
}
